import { Sparkles } from 'lucide-react'
import { useAgentLabels } from '@/hooks'

interface Props {
  resolvedModel: string
  resolvedEffort?: string
  defaultModel?: string
  defaultEffort?: string
  defaultSource?: 'workflow' | 'agent'
}

const DEFAULT_LABELS: Record<'workflow' | 'agent', string> = {
  workflow: 'workflow default',
  agent: 'agent default',
}

export function AgentNodeLineageBanner({ resolvedModel, resolvedEffort, defaultModel, defaultEffort, defaultSource }: Props) {
  const { modelLabel, effortLabel } = useAgentLabels()
  const haveDefault = !!defaultModel
  const effortDiffers = !!resolvedEffort && !!defaultEffort && resolvedEffort !== defaultEffort
  const isDifferentFromDefault =
    haveDefault && (resolvedModel !== defaultModel || effortDiffers)
  const defaultLabel = (defaultSource && DEFAULT_LABELS[defaultSource]) || 'default'

  const tone = isDifferentFromDefault
    ? 'border-warning-foreground bg-warning text-warning-foreground'
    : 'border-info-foreground bg-info text-info-foreground'

  const iconTone = isDifferentFromDefault ? 'text-warning-foreground' : 'text-info-foreground'

  const resolvedText = resolvedEffort
    ? `${modelLabel(resolvedModel)} · ${effortLabel(resolvedEffort)} effort`
    : modelLabel(resolvedModel)
  const defaultText = haveDefault
    ? defaultEffort
      ? `${modelLabel(defaultModel!)} · ${effortLabel(defaultEffort)}`
      : modelLabel(defaultModel!)
    : null

  let lineage: React.ReactNode
  if (!haveDefault) {
    lineage = <>Used <strong>{resolvedText}</strong>.</>
  } else if (!isDifferentFromDefault) {
    lineage = <>Used <strong>{resolvedText}</strong> — {defaultLabel}.</>
  } else {
    lineage = <>Used <strong>{resolvedText}</strong> — override ({defaultLabel} for this node was {defaultText}).</>
  }

  return (
    <div
      className={`mt-2 flex items-start gap-2 rounded border p-2 text-xs ${tone}`}
      role="status"
      data-testid="agent-node-lineage"
    >
      <Sparkles className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconTone}`} aria-hidden />
      <div>{lineage}</div>
    </div>
  )
}
